import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@thuthi/ui/components/sonner";
import { Copy, Globe, X } from "@thuthi/ui/icons";
import { Button } from "@thuthi/ui/components/button";
import { Card } from "@thuthi/ui/components/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@thuthi/ui/components/dialog";
import { Input } from "@thuthi/ui/components/input";
import { Label } from "@thuthi/ui/components/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@thuthi/ui/components/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@thuthi/ui/components/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@thuthi/ui/components/tooltip";
import routes from "./routes";
import { copyToClipboard, formatShortDateTime, openUrl } from "../app/utils";
import accountApi, { Role } from "../app/AccountApi";
import { Pref, PrefGroup } from "./Pref";
import { AccountContext } from "./App";
import DialogFooter from "./DialogFooter";
import { Paragraph } from "./styles";
import { UnauthorizedError } from "../app/errors";
import session from "../app/Session";
import type { AccountToken } from "../app/types";

/**
 * 계정 화면 — 로그인은 중앙 인증(auth.thuthi.kr)이 하므로 여기엔 사용자 이름과 발행 토큰만 있다.
 * 발행 토큰(/api/v2/tokens)은 다른 PC 의 curl·스크립트가 v2 발행(`POST /api/v2/messages`, multipart)이나 옛 경로로 보낼 때 쓴다.
 * 예전 ntfy 계정 기능(비밀번호·이메일·전화·요금제·계정 삭제)은 백엔드가 없어 뺐다.
 */

interface AccountDialogProps {
  open: boolean;
  onClose: () => void;
}

// 모바일(sm 미만)에서는 다이얼로그를 전체 화면으로 펼친다.
const dialogFullScreenSm =
  "max-sm:top-0 max-sm:left-0 max-sm:h-dvh max-sm:max-w-full max-sm:translate-x-0 max-sm:translate-y-0 " +
  "max-sm:content-start max-sm:overflow-y-auto max-sm:rounded-none max-sm:border-none";

const handleError = async (e: unknown, setError: (s: string) => void) => {
  console.log(`[Account] Error`, e);
  if (e instanceof UnauthorizedError) {
    await session.resetAndRedirect(routes.login);
  } else {
    setError((e as Error).message);
  }
};

const Account = () => {
  if (!session.exists()) {
    window.location.href = routes.app;
    return <></>;
  }
  return (
    <div className="mx-auto my-6 flex w-full max-w-4xl flex-col gap-6">
      <Basics />
      <Tokens />
    </div>
  );
};

const Basics = () => {
  const { t } = useTranslation();
  const { account } = useContext(AccountContext);
  const labelId = "prefUsername";
  return (
    <Card className="gap-4 p-6" aria-label={t("account_basics_title")}>
      <h2 className="text-2xl font-semibold tracking-tight">{t("account_basics_title")}</h2>
      <PrefGroup>
        <Pref labelId={labelId} title={t("account_basics_username_title")} description="중앙 로그인(auth.thuthi.kr) 계정이다.">
          <div aria-labelledby={labelId}>
            {account?.username ?? session.username()}
            {account?.role === Role.ADMIN && (
              <>
                {" "}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-default">👑</span>
                  </TooltipTrigger>
                  <TooltipContent>{t("account_basics_username_admin_tooltip")}</TooltipContent>
                </Tooltip>
              </>
            )}
          </div>
        </Pref>
      </PrefGroup>
    </Card>
  );
};

const Tokens = () => {
  const { t } = useTranslation();
  const { account } = useContext(AccountContext);
  const [dialogKey, setDialogKey] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const tokens = account?.tokens || [];

  return (
    <Card className="gap-2 p-6" aria-label={t("account_tokens_title")}>
      <h2 className="text-2xl font-semibold tracking-tight">발행 토큰</h2>
      <Paragraph>
        다른 PC 의 스크립트가 이 서버로 알림을 보낼 때 쓰는 토큰이다. 예:{" "}
        <code className="bg-muted rounded px-1 text-[0.85em] break-all">
          curl -H &quot;Authorization: Bearer tk_…&quot; -F topic=토픽 -F title=제목 -F message=내용 -F files=@사진.jpg {window.location.origin}/api/v2/messages
        </code>
      </Paragraph>
      {tokens.length > 0 && <TokensTable tokens={tokens} />}
      <div className="pt-2">
        <Button
          variant="outline"
          onClick={() => {
            setDialogKey((prev) => prev + 1);
            setDialogOpen(true);
          }}
        >
          {t("account_tokens_table_create_token_button")}
        </Button>
      </div>
      <TokenCreateDialog key={`tokenDialogCreate${dialogKey}`} open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </Card>
  );
};

const TokensTable = ({ tokens }: { tokens: AccountToken[] }) => {
  const { t, i18n } = useTranslation();
  const [deleteToken, setDeleteToken] = useState<AccountToken | null>(null);
  const sorted = [...tokens].sort((a, b) => (a.label || "").localeCompare(b.label || ""));

  const handleCopy = async (token: string) => {
    await copyToClipboard(token);
    toast.success(t("account_tokens_table_copied_to_clipboard"));
  };

  return (
    <>
      <div className="w-full overflow-x-auto">
        <Table aria-label={t("account_tokens_title")}>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-0">{t("account_tokens_table_token_header")}</TableHead>
              <TableHead>{t("account_tokens_table_label_header")}</TableHead>
              <TableHead>{t("account_tokens_table_expires_header")}</TableHead>
              <TableHead>{t("account_tokens_table_last_access_header")}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((token) => (
              <TableRow key={token.token} className="last:border-b-0">
                <TableCell className="whitespace-nowrap pl-0">
                  <span className="font-mono text-[0.9rem]">{token.token.slice(0, 12)}</span>…
                  <Button variant="ghost" size="icon" className="size-8" onClick={() => handleCopy(token.token)} aria-label={t("common_copy_to_clipboard")}>
                    <Copy />
                  </Button>
                </TableCell>
                <TableCell>{token.label || "-"}</TableCell>
                <TableCell className="whitespace-nowrap">
                  {token.expires ? formatShortDateTime(token.expires, i18n.language) : <em>{t("account_tokens_table_never_expires")}</em>}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <div className="flex items-center">
                    {token.last_access ? <span>{formatShortDateTime(token.last_access, i18n.language)}</span> : <em>-</em>}
                    {token.last_origin && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8"
                            onClick={() => openUrl(`https://whatismyipaddress.com/ip/${token.last_origin}`)}
                          >
                            <Globe />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t("account_tokens_table_last_origin_tooltip", { ip: token.last_origin })}</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    onClick={() => setDeleteToken(token)}
                    aria-label={t("account_tokens_dialog_title_delete")}
                  >
                    <X />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <TokenDeleteDialog open={!!deleteToken} token={deleteToken} onClose={() => setDeleteToken(null)} />
    </>
  );
};

const TokenCreateDialog = (props: AccountDialogProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState("");
  const [label, setLabel] = useState("");
  const [expires, setExpires] = useState(0);
  const [created, setCreated] = useState<string | null>(null);

  const handleSubmit = async () => {
    try {
      const token = await accountApi.createToken(label, expires);
      setCreated(token.token);
    } catch (e) {
      await handleError(e, setError);
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent className={`sm:max-w-xl ${dialogFullScreenSm}`} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{t("account_tokens_dialog_title_create")}</DialogTitle>
        </DialogHeader>
        {created ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm">토큰을 만들었다. 표에서도 언제든 복사할 수 있다.</p>
            <div className="flex items-center gap-1">
              <code className="bg-muted min-w-0 flex-1 rounded px-2 py-1.5 font-mono text-sm break-all">{created}</code>
              <Button
                variant="ghost"
                size="icon"
                onClick={async () => {
                  await copyToClipboard(created);
                  toast.success(t("account_tokens_table_copied_to_clipboard"));
                }}
                aria-label={t("common_copy_to_clipboard")}
              >
                <Copy />
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="token-label">{t("account_tokens_dialog_label")}</Label>
              <Input id="token-label" type="text" value={label} placeholder="예: 노트북" onChange={(ev) => setLabel(ev.target.value)} />
            </div>
            <Select value={String(expires)} onValueChange={(value) => setExpires(parseInt(value, 10))}>
              <SelectTrigger className="w-full" aria-label={t("account_tokens_dialog_expires_label")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">{t("account_tokens_dialog_expires_never")}</SelectItem>
                <SelectItem value="259200">{t("account_tokens_dialog_expires_x_days", { days: 3 })}</SelectItem>
                <SelectItem value="2592000">{t("account_tokens_dialog_expires_x_days", { days: 30 })}</SelectItem>
                <SelectItem value="7776000">{t("account_tokens_dialog_expires_x_days", { days: 90 })}</SelectItem>
                <SelectItem value="31536000">{t("account_tokens_dialog_expires_x_days", { days: 365 })}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <DialogFooter status={error}>
          {created ? (
            <Button onClick={props.onClose}>닫기</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={props.onClose}>
                {t("account_tokens_dialog_button_cancel")}
              </Button>
              <Button onClick={handleSubmit}>{t("account_tokens_dialog_button_create")}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface TokenDeleteDialogProps extends AccountDialogProps {
  token: AccountToken | null;
}

const TokenDeleteDialog = (props: TokenDeleteDialogProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    try {
      await accountApi.deleteToken(props.token!.token);
      props.onClose();
    } catch (e) {
      await handleError(e, setError);
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("account_tokens_delete_dialog_title")}</DialogTitle>
          <DialogDescription>
            {props.token?.label ? `'${props.token.label}' ` : ""}토큰을 지우면 그 토큰으로 보내던 스크립트가 바로 401 을 받는다.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter status={error}>
          <Button variant="ghost" onClick={props.onClose}>
            {t("common_cancel")}
          </Button>
          <Button variant="destructive" onClick={handleSubmit}>
            {t("account_tokens_delete_dialog_submit_button")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Account;
